/**
 * Trust Request Engine — Institution "Trust Requests" hub.
 *
 * Wires directly into /api/trust-requests. Everything begins with a
 * Trust Request. This page replaces the old placeholder RequestsHub —
 * it now shows live data, a live lifecycle KPI strip, filter tabs and
 * a NewRequestModal launcher.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";
import { formatDistanceToNow } from "date-fns";
import {
  Send, Plus, KeyRound, Fingerprint, ShieldCheck, GraduationCap,
  ClipboardCheck, BookOpen, Dice5, Package, Circle, ArrowRight,
} from "lucide-react";
import NewTrustRequestModal from "@/pages/requests/NewTrustRequestModal";

const NAVY = "#0A1F44";
const GOLD = "#C9A24A";

// ─── Request type registry (icon + copy) ─────────────────────
const REQUEST_TYPES = {
  identity_verification: { icon: Fingerprint,   label: "Identity Verification" },
  consent:               { icon: KeyRound,      label: "Consent" },
  kyc:                   { icon: ShieldCheck,   label: "KYC" },
  affordability:         { icon: ClipboardCheck,label: "Affordability" },
  product_understanding: { icon: BookOpen,      label: "Product Understanding" },
  learning_assignment:   { icon: GraduationCap, label: "Learning Assignment" },
  trust_package:         { icon: Package,       label: "Trust Package" },
  responsible_gaming_review: { icon: Dice5,     label: "Responsible Gaming Review" },
};

// ─── Status → tone mapping ───────────────────────────────────
const STATUS_TONE = {
  draft:               { label: "Draft",               bg: "#F1F5F9", color: "#334155" },
  sent:                { label: "Sent",                bg: "#DBEAFE", color: "#1E40AF" },
  consumer_accepted:   { label: "Accepted",            bg: "#E0E7FF", color: "#3730A3" },
  consumer_responding: { label: "Consumer Responding", bg: "#FEF3C7", color: "#92400E" },
  institution_review:  { label: "Institution Review",  bg: "#CCFBF1", color: "#0F766E" },
  completed:           { label: "Completed",           bg: "#D1FAE5", color: "#065F46" },
  declined:            { label: "Declined",            bg: "#FEE2E2", color: "#7F1D1D" },
  expired:             { label: "Expired",             bg: "#FEE2E2", color: "#7F1D1D" },
  cancelled:           { label: "Cancelled",           bg: "#F1F5F9", color: "#475569" },
  failed:              { label: "Failed",              bg: "#FEE2E2", color: "#7F1D1D" },
};

// ─── Lifecycle summary lanes ──────────────────────────────────
const LANES = [
  { key: "sent",                label: "Requested" },
  { key: "consumer_accepted",   label: "Accepted" },
  { key: "consumer_responding", label: "Consumer Responding" },
  { key: "institution_review",  label: "Institution Reviewing" },
  { key: "completed",           label: "Completed" },
  { key: "expired",             label: "Expired" },
];

export default function RequestsHub() {
  const [requests, setRequests] = useState([]);
  const [kpis, setKpis] = useState(null);
  const [filter, setFilter] = useState("all");
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [reqs, k] = await Promise.all([
        api.get("/trust-requests").then((r) => r.data),
        api.get("/trust-requests/kpis").then((r) => r.data).catch(() => null),
      ]);
      setRequests(reqs);
      setKpis(k);
    } catch (e) {
      toast.error("Failed to load Trust Requests");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    if (filter === "all") return requests;
    if (filter === "open") {
      const open = new Set(["sent", "consumer_accepted", "consumer_responding", "institution_review"]);
      return requests.filter((r) => open.has(r.status));
    }
    return requests.filter((r) => r.status === filter);
  }, [requests, filter]);

  return (
    <div className="max-w-6xl mx-auto p-6 lg:p-10 space-y-8" data-testid="requests-hub">
      {/* Header */}
      <header>
        <div className="text-[10px] uppercase tracking-[0.22em] font-semibold" style={{ color: GOLD }}>
          Institution Workspace · Trust Requests
        </div>
        <div className="flex items-start justify-between gap-4 flex-wrap mt-1">
          <div>
            <h1 className="font-display text-3xl font-semibold" style={{ color: NAVY }}>Trust Requests</h1>
            <p className="text-[15px] text-slate-600 max-w-3xl mt-2 leading-relaxed">
              Everything starts with a Trust Request. Identity, Consent, KYC, Learning, Affordability — one universal object, one lifecycle, one audit trail.
            </p>
          </div>
          <button
            data-testid="new-request-btn"
            onClick={() => setModalOpen(true)}
            className="inline-flex items-center gap-2 rounded-xl px-4 py-2.5 text-[13px] font-semibold text-white shadow-sm hover:shadow"
            style={{ background: NAVY }}
          >
            <Plus className="w-4 h-4" /> New Trust Request
          </button>
        </div>
      </header>

      {/* Live lifecycle KPI strip ───────────────────────────── */}
      <section data-testid="request-lifecycle">
        <div className="text-[11px] uppercase tracking-widest font-semibold text-slate-500 mb-2">
          Lifecycle
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-6 gap-2">
          {LANES.map((lane) => {
            const tone = STATUS_TONE[lane.key] || STATUS_TONE.draft;
            const count = kpis?.by_status?.[lane.key] ?? null;
            return (
              <button
                key={lane.key}
                data-testid={`lifecycle-${lane.key}`}
                onClick={() => setFilter(lane.key)}
                className={`text-left rounded-2xl bg-white border p-3 transition ${
                  filter === lane.key ? "border-slate-400 shadow" : "border-slate-100 hover:border-slate-300"
                }`}
              >
                <span
                  className="text-[9px] uppercase tracking-widest font-mono px-1.5 py-0.5 rounded"
                  style={{ background: tone.bg, color: tone.color }}
                >
                  {tone.label}
                </span>
                <div className="font-display text-2xl font-semibold mt-2" style={{ color: count === null ? "#94A3B8" : NAVY }}>
                  {count === null ? "—" : count}
                </div>
              </button>
            );
          })}
        </div>
      </section>

      {/* Institution KPI panel (per Sprint 7 spec) ─────────── */}
      {kpis && (
        <section data-testid="request-kpis" className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          <KpiTile label="Open"                value={kpis.open} accent={NAVY} />
          <KpiTile label="Completed today"     value={kpis.completed_today} accent="#065F46" />
          <KpiTile label="Awaiting consumer"   value={kpis.awaiting_consumer} accent="#92400E" />
          <KpiTile label="Awaiting review"     value={kpis.awaiting_review} accent="#0F766E" />
        </section>
      )}

      {/* Filter chips */}
      <div className="flex flex-wrap gap-2" data-testid="request-filters">
        {[
          { k: "all",       l: "All" },
          { k: "open",      l: "Open" },
          { k: "completed", l: "Completed" },
          { k: "expired",   l: "Expired" },
          { k: "cancelled", l: "Cancelled" },
        ].map((f) => (
          <button
            key={f.k}
            data-testid={`filter-${f.k}`}
            onClick={() => setFilter(f.k)}
            className={`text-[12px] font-semibold px-3 py-1.5 rounded-full border transition ${
              filter === f.k
                ? "text-white border-transparent"
                : "text-slate-600 bg-white border-slate-200 hover:bg-slate-50"
            }`}
            style={filter === f.k ? { background: NAVY } : {}}
          >
            {f.l}
          </button>
        ))}
      </div>

      {/* Request list ──────────────────────────────────────── */}
      <section data-testid="request-list" className="space-y-2">
        {loading && <div className="text-sm text-slate-500">Loading…</div>}
        {!loading && filtered.length === 0 && (
          <div className="rounded-2xl border-2 border-dashed border-slate-200 p-8 text-center">
            <Send className="w-6 h-6 text-slate-300 mx-auto" />
            <div className="text-[14px] font-semibold text-slate-600 mt-2">No Trust Requests yet</div>
            <div className="text-[12px] text-slate-500 mt-1">Click <strong>New Trust Request</strong> to send your first one.</div>
          </div>
        )}
        {!loading && filtered.map((r) => <RequestRow key={r.request_id} req={r} />)}
      </section>

      {/* New request modal */}
      {modalOpen && (
        <NewTrustRequestModal
          onClose={() => setModalOpen(false)}
          onCreated={() => { setModalOpen(false); load(); }}
        />
      )}
    </div>
  );
}

/* ─── Row + tile primitives ────────────────────────────── */

function RequestRow({ req }) {
  const type = REQUEST_TYPES[req.request_type] || { icon: Circle, label: req.request_type };
  const Icon = type.icon;
  const tone = STATUS_TONE[req.status] || STATUS_TONE.draft;
  const createdAgo = req.created_at
    ? formatDistanceToNow(new Date(req.created_at), { addSuffix: true })
    : "";
  return (
    <Link
      to={`/trust-requests/${req.request_id}`}
      data-testid={`request-row-${req.request_id}`}
      className="flex items-center gap-3 rounded-2xl bg-white border border-slate-100 p-4 hover:border-slate-300 hover:shadow-sm transition group"
    >
      <div className="w-9 h-9 rounded-xl flex items-center justify-center shrink-0" style={{ background: `${GOLD}22`, color: GOLD }}>
        <Icon className="w-4 h-4" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <div className="font-semibold text-[14px] truncate" style={{ color: NAVY }}>
            {req.purpose}
          </div>
          <span
            className="text-[9px] uppercase tracking-widest font-mono px-1.5 py-0.5 rounded"
            style={{ background: tone.bg, color: tone.color }}
          >
            {tone.label}
          </span>
        </div>
        <div className="text-[11px] text-slate-500 mt-0.5">
          {type.label}
          {req.consumer_display_name ? ` · ${req.consumer_display_name}` : ""}
          {req.related_requests?.length > 0 ? ` · ${req.related_requests.length} bundled` : ""}
          {createdAgo ? ` · ${createdAgo}` : ""}
        </div>
      </div>
      <ArrowRight className="w-4 h-4 text-slate-300 group-hover:text-slate-500 shrink-0" />
    </Link>
  );
}

function KpiTile({ label, value, accent }) {
  const empty = value === null || value === undefined;
  return (
    <div
      data-testid={`kpi-${label.toLowerCase().replace(/\s+/g, "-")}`}
      className="rounded-2xl bg-white border border-slate-100 shadow-sm p-4"
    >
      <div className="text-[10px] uppercase tracking-widest font-semibold text-slate-500">
        {label}
      </div>
      <div className="font-display text-2xl font-semibold mt-1" style={{ color: empty ? "#94A3B8" : accent }}>
        {empty ? "—" : value}
      </div>
    </div>
  );
}
